import React, { useEffect, useRef } from "react";
import "leaflet/dist/leaflet.css";

// Free, interactive locations map: Leaflet + OpenStreetMap tiles, no API key or
// account required. Renders one map with a branded pin per office and a popup
// with the address and a directions link. Leaflet touches `window`, so it is
// imported dynamically inside useEffect (never during Gatsby SSR).
const directions = (loc) =>
  `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
    `${loc.address}, ${loc.detail}`
  )}`;

const LocationsMap = ({ locations = [] }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return undefined;
    let cancelled = false;

    import("leaflet").then((mod) => {
      const L = mod.default || mod;
      if (cancelled || !containerRef.current || mapRef.current) return;

      const map = L.map(containerRef.current, {
        scrollWheelZoom: false,
        zoomControl: true,
      });
      mapRef.current = map;

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 18,
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      }).addTo(map);

      const pin = L.divIcon({
        className: "rh-map-pin",
        html: '<span class="rh-map-pin__dot"></span>',
        iconSize: [28, 28],
        iconAnchor: [14, 27],
        popupAnchor: [0, -24],
      });

      const points = [];
      locations.forEach((loc) => {
        if (typeof loc.lat !== "number" || typeof loc.lng !== "number") return;
        points.push([loc.lat, loc.lng]);
        L.marker([loc.lat, loc.lng], { icon: pin, title: loc.city, alt: loc.city })
          .addTo(map)
          .bindPopup(
            `<div class="rh-map-popup"><strong>${loc.city}${
              loc.tag ? ` &middot; ${loc.tag}` : ""
            }</strong><span>${loc.address}<br>${loc.detail}</span>` +
              `<a href="${directions(loc)}" target="_blank" rel="noopener noreferrer">Get directions &rarr;</a></div>`
          );
      });

      if (points.length > 1) {
        map.fitBounds(points, { padding: [45, 45], maxZoom: 9 });
      } else if (points.length === 1) {
        map.setView(points[0], 10);
      } else {
        map.setView([42.55, -85.2], 8); // fallback: West Michigan
      }
    });

    return () => {
      cancelled = true;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [locations]);

  return (
    <div
      ref={containerRef}
      className="rh-locations__map"
      role="img"
      aria-label="Map of Roach Home Improvement offices in Battle Creek, Wyoming, and Jackson, Michigan"
    />
  );
};

export default LocationsMap;
